import React, { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "antd";
import styled from "styled-components";

import { useApi, ApiNames } from "../../hooks/api";
import { useAutoRefetch } from "./useAutoRefetch";
import { sumWaitingTasks } from "./taskHelpers";
import { WHITE, GRAY_200, GRAY_500, GRAY_800, GRAY_900, SUCCESS_600 } from "../../constant/colors";

const DAY_MS = 24 * 60 * 60 * 1000;
// The header's range window for the "· 30 days" tiles (the "need you" /
// "agent working" tiles are current-state and ignore it) — matches the
// dashboard's default reporting window.
const RANGE_DAYS = 30;

// ─── Visual identity ────────────────────────────────────────────────────
//
// Four quiet stat cards, left-aligned (unlike the dashboard's centered KPI
// tiles — this strip is a page HEADER, so it reads with the page's text).
// The first card — "Need you", the only number that demands action — is the
// single point of emphasis: inverted on the dark ink the rest of the product
// reserves for headline text (GRAY_800/900), so the strip carries exactly one
// focal point and everything else stays calm.

const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
`;

const StatTile = styled.div`
  background: ${WHITE};
  border: 1px solid ${GRAY_200};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(16, 24, 40, 0.06);
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatTileDark = styled(StatTile)`
  background: ${GRAY_800};
  border-color: ${GRAY_800};
`;

const StatLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${(p) => (p.$onDark ? "rgba(255, 255, 255, 0.65)" : GRAY_500)};
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.15;
  color: ${(p) => p.$color || (p.$onDark ? WHITE : GRAY_900)};
`;

const StatSub = styled.div`
  font-size: 12px;
  color: ${(p) => (p.$onDark ? "rgba(255, 255, 255, 0.55)" : GRAY_500)};
`;

/**
 * Compact general-statistics header shared by the Tasks hub and the home-page
 * agentic summary — the at-a-glance answer to "how much needs me, how much is
 * the agent carrying":
 *
 *   - **Need you** (current)      — every run parked on a navigator/operator
 *     task: consent decisions + EE reviews + manual EMR fills + screenshot
 *     uploads. Matches the Tasks hub's section counts summed. The one
 *     emphasized (dark) tile — the only number that demands action.
 *   - **Agent working** (current) — runs in flight, no human needed.
 *   - **Completed · 30 days**     — runs that finished without crashing.
 *   - **Enrolled · 30 days**      — runs that ended with at least one program
 *     enrolled (full + partial).
 *
 * Self-contained: fetches `AgenticRunStats` itself (initial + 1m auto-refresh,
 * paused while the tab is hidden) so each surface can mount it without
 * threading dashboard state around. Renders a skeleton while the FIRST load is
 * in flight and nothing on error — a stats strip is decoration, never worth a
 * blocking error state on the page hosting it.
 */
const AgenticStatsHeader = () => {
  const { t } = useTranslation();
  // Slot 3 (isFetching) unused: the strip shows a skeleton only before the
  // FIRST payload; later refreshes swap data in place.
  const [fetchStats, result, , fetchError] = useApi(ApiNames.AgenticRunStats);

  // Same identity dance as the dashboard: `useApi` hands back a fresh invoke
  // per render, so the auto-refetch is driven off a ref + a stable callback.
  const fetchStatsRef = useRef(fetchStats);
  fetchStatsRef.current = fetchStats;
  const refetch = useCallback(() => {
    const to = new Date();
    const from = new Date(to.getTime() - RANGE_DAYS * DAY_MS);
    fetchStatsRef.current({ from: from.toISOString(), to: to.toISOString() });
  }, []);
  useAutoRefetch(refetch);

  if (fetchError && !result) return null;
  if (!result) {
    return (
      <StatRow data-testid="agentic-stats-header-loading">
        <Skeleton.Button active block style={{ height: 88 }} />
      </StatRow>
    );
  }

  const needYou = sumWaitingTasks(result.waiting);
  const inFlight = result.byOutcome?.in_flight ?? 0;
  const totalRuns = result.totalRuns ?? 0;
  const failed = result.byOutcome?.failure ?? 0;
  const completed = Math.max(0, totalRuns - inFlight - failed);
  const enrolled = (result.byOutcome?.enrolled ?? 0) + (result.byOutcome?.enrollment_partial ?? 0);

  const tiles = [
    {
      key: "agent_working",
      label: t("agentic_stats.agent_working"),
      value: inFlight,
      sub: t("agentic_stats.agent_working_sub")
    },
    {
      key: "completed",
      label: t("agentic_stats.completed_30d"),
      value: completed,
      sub: t("agentic_stats.last_30_days")
    },
    {
      key: "enrolled",
      label: t("agentic_stats.enrolled_30d"),
      value: enrolled,
      valueColor: enrolled > 0 ? SUCCESS_600 : undefined,
      sub: t("agentic_stats.last_30_days")
    }
  ];

  return (
    <StatRow data-testid="agentic-stats-header">
      <StatTileDark data-testid="agentic-stats-need_you">
        <StatLabel $onDark>{t("agentic_stats.need_you")}</StatLabel>
        <StatValue $onDark>{needYou.toLocaleString()}</StatValue>
        <StatSub $onDark>{t("agentic_stats.need_you_sub")}</StatSub>
      </StatTileDark>
      {tiles.map((tile) => (
        <StatTile key={tile.key} data-testid={`agentic-stats-${tile.key}`}>
          <StatLabel>{tile.label}</StatLabel>
          <StatValue $color={tile.valueColor}>{tile.value.toLocaleString()}</StatValue>
          <StatSub>{tile.sub}</StatSub>
        </StatTile>
      ))}
    </StatRow>
  );
};

export { AgenticStatsHeader };
